import { useEffect, useRef, useState } from 'react';
import { deleteUpload, releaseUpload, type InspectionFlag } from '../api/inspections';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import {
  captionFor,
  kindLabel,
  nextUnreviewedIndex,
  qcBadgeText,
  type ReviewItem,
} from './inspectionGroups';
import { FIT_VIEW, STEP, panBy, zoomAt, type PhotoView } from './photoView';

const FLAG_ORDER: InspectionFlag[] = ['correct', 'problem', 'useless', 'repeat', 'mismatch'];

export interface PhotoViewerProps {
  /** The whole slideshow: photos and the context cards between their groups, in the order it steps through. */
  items: ReviewItem[];
  index: number;
  onClose: () => void;
  onIndex: (index: number) => void;
  onToggleFlag: (uploadId: number, flag: InspectionFlag) => void;
  onNote: (uploadId: number, note: string) => void;
  savingNote: boolean;
  /**
   * Whether the first mark on a capture moves on to the next one that waits
   * (`useReviewFlow`), and the person's own switch for it. Absent, the viewer
   * shows no switch and nothing moves by itself.
   */
  advance?: boolean;
  onAdvance?: (on: boolean) => void;
  /**
   * An admin released a withheld picture or deleted it (docs/RUNBOOK.md,
   * "Deleting one upload"): the page updates its own list.
   */
  onModerated?: (uploadId: number, outcome: 'released' | 'deleted') => void;
}

/**
 * The full-screen viewer for a closeup inspection tour's slideshow: a photo
 * (wheel or pinch to zoom, drag to pan) or a context card between two groups
 * of them, stepping with ←/→, Esc to close. A photo's caption is in three
 * parts — what it is, why it is worth a look, what to look at — and its five
 * flags and note box live inside the viewer too, so a reviewer never leaves
 * the picture to mark it. The pan/zoom transform is `photoView.ts`'s pure
 * maths (the terrain map's own `mapView.ts`, generalised to a rectangular
 * box), applied as a single CSS transform.
 */
export function PhotoViewer({
  items,
  index,
  onClose,
  onIndex,
  onToggleFlag,
  onNote,
  savingNote,
  advance,
  onAdvance,
  onModerated,
}: PhotoViewerProps) {
  const t = ca.inspectionReviewPage;
  const isAdmin = useAuth().me.roles.includes('ADMIN');
  const [moderationError, setModerationError] = useState<string | null>(null);
  const item = items[index];
  const capture = item?.type === 'capture' ? item.capture : null;
  const [view, setView] = useState<PhotoView>(FIT_VIEW);
  const [note, setNote] = useState(capture?.note ?? '');
  const box = useRef<HTMLDivElement | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const captured = useRef<number | null>(null);
  const dragged = useRef(false);
  const pinchSpread = useRef<number | null>(null);
  const itemKey = item
    ? item.type === 'capture'
      ? `c${item.capture.uploadId}`
      : `x${item.key}`
    : '';

  // A new item opens at rest, and a capture's own note replaces whatever the
  // last one held; a save answering back with the same upload's own note (no
  // item change) is a no-op restate, not a reason to reset the pan/zoom.
  // Never on mounting, when the box already holds it: that first effect ran
  // after the first keys typed on a busy machine and emptied the draft.
  const held = `${capture?.uploadId ?? ''}|${capture?.note ?? ''}`;
  const shown = useRef(held);
  useEffect(() => {
    if (shown.current === held) return;
    shown.current = held;
    setNote(capture?.note ?? '');
  }, [held, capture?.note]);

  useEffect(() => {
    setView(FIT_VIEW);
  }, [itemKey]);

  // The picture after this one is asked for while this one is looked at, so
  // stepping on shows it at once (a capture is some 400 kB of JPEG).
  useEffect(() => {
    const next = items.slice(index + 1).find((it) => it.type === 'capture');
    if (next?.type === 'capture' && next.capture.url && !next.capture.withheld) {
      const ahead = new Image();
      ahead.src = next.capture.url;
    }
  }, [items, index]);

  // A note being typed is saved when the picture changes under it or the
  // viewer closes (Escape, the auto-advance after a mark), which unmount or
  // repurpose the box without a blur: the note was lost (the owner, 2026-09-30).
  const noteNow = useRef(note);
  noteNow.current = note;
  const onNoteNow = useRef(onNote);
  onNoteNow.current = onNote;
  const uploadId = capture?.uploadId;
  const savedNote = capture?.note ?? '';
  useEffect(() => {
    if (uploadId === undefined) return;
    // The note as it stood when this picture opened; one saved since is not
    // saved twice (`useReviewFlow.saveNote` skips a note equal to the capture's).
    const opened = savedNote;
    return () => {
      if (noteNow.current !== opened) onNoteNow.current(uploadId, noteNow.current);
    };
    // Only the picture changing (or the viewer closing) is the moment to save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // The arrows move the caret in the note, not the picture: turning the
      // page mid-sentence dropped what was typed (the owner, 2026-09-30).
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT')) return;
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      else if (e.key === 'ArrowRight' && index < items.length - 1) onIndex(index + 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onIndex]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      setView((v) =>
        zoomAt(
          v,
          Math.exp(-e.deltaY * 0.002),
          e.clientX - rect.left,
          e.clientY - rect.top,
          rect.width,
          rect.height,
        ),
      );
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  }, []);

  if (!item) return null;

  function size() {
    const rect = box.current?.getBoundingClientRect();
    return { width: rect?.width ?? 1, height: rect?.height ?? 1 };
  }

  function pointerDown(e: React.PointerEvent<HTMLDivElement>) {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    dragged.current = false;
  }

  function pointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const from = pointers.current.get(e.pointerId);
    if (!from) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const { width, height } = size();
    const points = [...pointers.current.values()];
    if (points.length >= 2) {
      const [a, b] = points;
      const rect = box.current?.getBoundingClientRect();
      const midX = (a.x + b.x) / 2 - (rect?.left ?? 0);
      const midY = (a.y + b.y) / 2 - (rect?.top ?? 0);
      const spread = Math.hypot(a.x - b.x, a.y - b.y);
      const factor = pinchSpread.current ? spread / pinchSpread.current : 1;
      pinchSpread.current = spread;
      setView((v) => zoomAt(v, factor, midX, midY, width, height));
      dragged.current = true;
      return;
    }
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;
    if (Math.abs(dx) + Math.abs(dy) > 2) {
      dragged.current = true;
      if (captured.current !== e.pointerId) {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        captured.current = e.pointerId;
      }
    }
    setView((v) => panBy(v, dx, dy, width, height));
  }

  function pointerUp(e: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchSpread.current = null;
    if (captured.current === e.pointerId) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      captured.current = null;
    }
  }

  function zoomBy(factor: number) {
    const { width, height } = size();
    setView((v) => zoomAt(v, factor, width / 2, height / 2, width, height));
  }

  function saveNote() {
    if (capture && note !== (capture.note ?? '')) onNote(capture.uploadId, note);
  }

  const caption = capture ? captionFor(capture) : null;
  const label = item.type === 'context' ? item.title : (capture?.label ?? '');
  // The count is of pictures: the cards between their groups are not something
  // to review, and "2 de 91" on the first photo of eighty-one read as a mistake.
  const pictures = items.filter((it) => it.type === 'capture');
  const position = items.slice(0, index + 1).filter((it) => it.type === 'capture').length;
  const pending = pictures.filter((it) => it.type === 'capture' && !it.capture.reviewedBy).length;
  const nextPending = nextUnreviewedIndex(items, index);

  return (
    <div className="photo-viewer" role="dialog" aria-modal="true" aria-label={label}>
      <div className="photo-viewer-top">
        <span className="muted small" role="status">
          {capture
            ? t.viewerProgress(position, pictures.length, pending)
            : t.viewerCount(position, pictures.length)}
        </span>
        <div className="row">
          {onAdvance && (
            <label className="check small photo-viewer-advance">
              <input
                type="checkbox"
                checked={advance ?? false}
                onChange={(e) => onAdvance(e.target.checked)}
              />
              {t.advance}
            </label>
          )}
          {nextPending !== null && nextPending !== index && (
            <button type="button" className="small" onClick={() => onIndex(nextPending)}>
              {t.nextPending}
            </button>
          )}
          {capture && (
            <>
              <button type="button" className="small" onClick={() => zoomBy(1 / STEP)}>
                −
              </button>
              <button type="button" className="small" onClick={() => zoomBy(STEP)}>
                +
              </button>
            </>
          )}
          <button type="button" className="small" onClick={onClose} aria-label={t.viewerClose}>
            {t.viewerClose}
          </button>
        </div>
      </div>
      <div className="photo-viewer-stage">
        <button
          type="button"
          className="photo-viewer-nav prev"
          aria-label={t.viewerPrev}
          disabled={index === 0}
          onClick={() => onIndex(index - 1)}
        >
          ‹
        </button>
        {capture ? (
          <div
            ref={box}
            className="photo-viewer-box"
            onPointerDown={pointerDown}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onPointerCancel={pointerUp}
            onClick={(e) => {
              if (dragged.current) {
                dragged.current = false;
                e.preventDefault();
              }
            }}
          >
            {capture.withheld && !isAdmin && (
              <p className="photo-viewer-withheld">{t.withheldPlayer}</p>
            )}
            {capture.url && (!capture.withheld || isAdmin) && (
              <img
                src={capture.url}
                alt={capture.label ?? ''}
                className="photo-viewer-img"
                style={{
                  transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                  transformOrigin: '0 0',
                }}
                draggable={false}
              />
            )}
          </div>
        ) : (
          <div className="photo-viewer-context">
            <h2>{item.type === 'context' ? item.title : ''}</h2>
            <ul>{item.type === 'context' && item.facts.map((f, i) => <li key={i}>{f}</li>)}</ul>
          </div>
        )}
        <button
          type="button"
          className="photo-viewer-nav next"
          aria-label={t.viewerNext}
          disabled={index === items.length - 1}
          onClick={() => onIndex(index + 1)}
        >
          ›
        </button>
      </div>
      {capture && caption && (
        <div className="photo-viewer-panel">
          <h3>{caption.what}</h3>
          {capture.stopKind && (
            <span className="chip small kind">{kindLabel(capture.stopKind)}</span>
          )}
          {caption.why && (
            <p className="muted small">
              <strong>{t.whyLabel}</strong> {caption.why}
            </p>
          )}
          {(caption.look || caption.expect) && (
            <p className="muted small">
              <strong>{t.lookLabel}</strong> {caption.look ?? caption.expect}
            </p>
          )}
          {capture.report && capture.report.length > 0 && (
            <pre className="capture-report">{capture.report.join('\n')}</pre>
          )}
          {(capture.agentVerdict || capture.agentNote) && (
            <p className={`agent-note verdict-${capture.agentVerdict ?? 'none'}`}>
              <strong>
                {t.agentNote}
                {capture.agentVerdict
                  ? ` · ${t.agentVerdicts[capture.agentVerdict] ?? capture.agentVerdict}`
                  : ''}
              </strong>{' '}
              {capture.agentNote}
            </p>
          )}
          {(capture.qcFlags?.length ?? 0) > 0 && (
            <div className="row capture-flags">
              {capture.qcFlags.map((f) => (
                <span key={f} className="chip qc small">
                  {qcBadgeText(f, capture.duplicateOfUploadId)}
                </span>
              ))}
            </div>
          )}
          {capture.withheld && isAdmin && (
            <div className="photo-viewer-moderation">
              <p className="small">
                <span className="chip small withheld">{t.withheld}</span> {t.withheldHint}
              </p>
              <div className="row">
                <button
                  type="button"
                  className="small"
                  onClick={() => {
                    setModerationError(null);
                    releaseUpload(capture.uploadId)
                      .then(() => onModerated?.(capture.uploadId, 'released'))
                      .catch((e) => setModerationError(String(e)));
                  }}
                >
                  {t.release}
                </button>
                <button
                  type="button"
                  className="small danger"
                  onClick={() => {
                    if (!window.confirm(t.removeConfirm)) return;
                    setModerationError(null);
                    deleteUpload(capture.uploadId)
                      .then(() => onModerated?.(capture.uploadId, 'deleted'))
                      .catch((e) => setModerationError(String(e)));
                  }}
                >
                  {t.remove}
                </button>
              </div>
              {moderationError && (
                <p className="error small" role="alert">
                  {t.moderationFailed} {moderationError}
                </p>
              )}
            </div>
          )}
          <div className="row photo-viewer-flags" role="group">
            {FLAG_ORDER.map((flag) => (
              <button
                key={flag}
                type="button"
                className={`chip ${capture.flags.includes(flag) ? 'on' : ''}`}
                aria-pressed={capture.flags.includes(flag)}
                disabled={!isAdmin}
                onClick={() => onToggleFlag(capture.uploadId, flag)}
              >
                {t.flags[flag] ?? flag}
              </button>
            ))}
          </div>
          <label className="photo-viewer-note">
            {t.noteLabel}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={saveNote}
              readOnly={!isAdmin}
              placeholder={isAdmin ? t.notePlaceholder : ''}
              maxLength={2000}
              rows={2}
            />
          </label>
          <p className="muted small">
            {savingNote
              ? t.saving
              : capture.reviewedBy
                ? t.reviewedBy(capture.reviewedBy)
                : t.unreviewed}
          </p>
        </div>
      )}
    </div>
  );
}
